import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, RouterModule } from '@angular/router';

import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  BrowserModule,
  provideClientHydration,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { lastValueFrom } from 'rxjs';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { routes } from './app.routes';
import { HeaderInterceptor } from './core/interceptors/header.interceptor';
import { CheckoutState } from './shared/store/states/checkout.state';
import { MasterState } from './shared/store/states/master.state';

import { ShoppingCartState } from './shared/store/states/shoppingCart.state';
import { SiteSettingState } from './shared/store/states/siteSetting.state';
import { TemplateConfigState } from './shared/store/states/templateConfig.state';
import { NgxStripeModule } from 'ngx-stripe';
import { provideToastr } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

export function appInitializerFactory(translate: TranslateService) {
  return () => {
    const supportedLangs = ['en', 'fr', 'es', 'ar'];
    translate.addLangs(supportedLangs);
    translate.setDefaultLang('en');
    const browserLang = translate.getBrowserLang();
    const langToUse = browserLang && supportedLangs.includes(browserLang.substring(0, 2))
      ? browserLang.substring(0, 2)
      : 'en';
    console.log('[APP_INITIALIZER] browserLang:', browserLang, '-> langToUse:', langToUse);
    return lastValueFrom(translate.use(langToUse)).then((translations) => {
      console.log('[APP_INITIALIZER] Translations loaded for:', langToUse, '- keys:', translations ? Object.keys(translations).length : 'null');
      return translations;
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true,
    },
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      CarouselModule,
      LoadingBarRouterModule,
      NgxsModule.forRoot([
        TemplateConfigState,
        ShoppingCartState,
        SiteSettingState,
        MasterState,
        CheckoutState,
      ]),
      NgxStripeModule.forRoot(''),
      NgxsModule.forFeature([]),
      RouterModule.forRoot(routes, {
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      NgxsStoragePluginModule.forRoot({
        key: [
          'templateConfig',
          'shoppingCart',
          'masters',
          'siteSetting',
          'checkout',
        ],
      }),
    ),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideClientHydration(),
    provideToastr({
      timeOut: 4000,
      positionClass: 'toast-top-right',
      preventDuplicates: false, // Allow same message to appear multiple times
      newestOnTop: true,
      maxOpened: 3, // Max 3 toasts at a time
      autoDismiss: true,
    }),

    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    // Proveer Firestore
    provideFirestore(() => getFirestore())
  ],
};
